import { type SubscriptionTerm } from '@cx-sdk/subs';
import { REPUB_CONFIG } from '@cx-web/repub-config';

import { type Repub } from '@cx-modules/constants';

import { actions, events, views } from '~/templates/auth/constants';
import { trackEvent } from '~/utils/dataLayer';

export type LowercaseSnakeCaseString<T extends string> =
  T extends Lowercase<T>
    ? T extends `${string}_${string}`
      ? string extends T
        ? never
        : T
      : string extends T
        ? never
        : T extends `${string} ${string}`
          ? never
          : T
    : never;

export const trackLoginSuccess = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { repub, userId }: { repub: Repub; userId: string },
) => {
  trackEvent(events.LOG_IN, {
    eventAction: actions.LOGIN_SUCCESS,
    eventLabel: 'Login Success',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.LOGIN,
    userId,
  });
};

export const trackLoginFailure = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { email, error, repub }: { email: string; error: string; repub: Repub },
) => {
  trackEvent(events.LOG_IN_ERROR, {
    eventAction: actions.LOGIN_ERROR,
    eventLabel: 'Login Error',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.LOGIN,
    email,
    loginError: error,
  });
};

export const trackSignupSuccess = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { repub, userId }: { repub: Repub; userId: string },
) => {
  trackEvent(events.ACCOUNT_CREATED, {
    eventAction: actions.SIGN_UP_SUCCESS,
    eventLabel: 'Signup Success',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.SIGN_UP,
    userId,
  });
};

export const trackSignupFailure = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { email, error, repub }: { email: string; error: string; repub: Repub },
) => {
  trackEvent(events.ACCOUNT_CREATED_ERROR, {
    eventAction: actions.SIGN_UP_ERROR,
    eventLabel: 'Signup Error',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.SIGN_UP,
    email,
    signupError: error,
  });
};

export const trackComponentWallCtaClick = ({
  feature,
  isUpgrade,
  price,
  selection,
  term,
}: {
  feature: string;
  isUpgrade: boolean;
  price: string;
  selection?: 'Essential' | 'Premium' | 'WhoScored Premium';
  term: SubscriptionTerm | undefined;
}) => {
  trackEvent('paywall_cta', {
    eventLabel: isUpgrade ? 'Upgrade CTA' : 'Paywall CTA',
    feature,
    pageUrl: window.location.href,
    product: `${term} - ${price}`,
    referrerUrl: document.referrer,
    selection,
    upgradeOrPurchase: isUpgrade ? 'Upgrade' : 'Purchase',
  });
};
